/**
 * Rahmen der Anwendung: Kopfzeile, Navigation, Hinweis auf wartende
 * Änderungen und auf eine neue Fassung.
 *
 * Die Navigation liegt am unteren Rand. Auf dem Handy erreicht der Daumen
 * die obere Bildschirmhälfte nicht, und der Prüfer hält das Gerät draußen in
 * einer Hand.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { darf, darfVerwalten, useKonto } from '../konto/KontoKontext'
import { beobachten, abarbeiten, type Sendestand } from '../offline/warteschlange'
import { Neufassung } from './Neufassung'
import { Einfuehrung } from './Einfuehrung'
import { Suche } from './Suche'
import { ROLLENTEXT } from '../api/typen'
import { Uebernahmestreifen } from './Uebernahme'
import { Versandhinweis } from './Versandhinweis'
import wortmarke from '../assets/spuer-wortmarke.png'
import {
  SymbolAblauf,
  SymbolBrief,
  SymbolHaus,
  SymbolKatalog,
  SymbolKunden,
  SymbolMenue,
  SymbolPerson,
  SymbolPruefung,
  SymbolSchliessen,
  SymbolUhr,
  SymbolWarteschlange,
  SymbolZahnrad,
} from './Symbole'

const NAVIGATION = [
  { pfad: '/', text: 'Übersicht', Symbol: SymbolHaus },
  { pfad: '/pruefungen', text: 'Prüfungen', Symbol: SymbolPruefung },
  { pfad: '/aufgaben', text: 'Fällig', Symbol: SymbolUhr },
  { pfad: '/kunden', text: 'Kunden', Symbol: SymbolKunden },
  { pfad: '/terminanfragen', text: 'Anfragen', Symbol: SymbolBrief },
]

export function Rahmen({ children }: { children: ReactNode }) {
  const { konto, abmelden, ohneGegenprobe } = useKonto()
  const [stand, setzeStand] = useState<Sendestand>({ laeuft: false, offen: 0, gescheitert: 0 })
  const [online, setzeOnline] = useState(navigator.onLine)
  const ort = useLocation()
  const [menueOffen, setzeMenueOffen] = useState(false)
  const [einfuehrung, setzeEinfuehrung] = useState(false)

  useEffect(() => beobachten(setzeStand), [])
  useEffect(() => setzeMenueOffen(false), [ort.pathname])

  useEffect(() => {
    const an = () => setzeOnline(true)
    const aus = () => setzeOnline(false)
    window.addEventListener('online', an)
    window.addEventListener('offline', aus)
    return () => {
      window.removeEventListener('online', an)
      window.removeEventListener('offline', aus)
    }
  }, [])

  // Der Prüfbogen bringt seine eigene Werkzeugleiste mit; die Navigation
  // würde dort im Weg stehen.
  const imBogen = /^\/pruefungen\/[^/]+\/bogen/.test(ort.pathname)

  return (
    <div className="flex min-h-dvh flex-col">
      {/* Der Übernahmestreifen steht ganz oben, noch über dem Hinweis auf
          eine neue Fassung: in wessen Namen man handelt, ist die wichtigere
          Information. */}
      <Uebernahmestreifen />

      {/* Direkt darunter: in wessen Namen man handelt, ist die wichtigere
          Information — ob Post hinausgeht, die zweitwichtigste. */}
      <Versandhinweis />

      {/* Nur hier, also nur für Angemeldete. Auf der Anmeldeseite hat der
          Hinweis niemanden, den er betrifft: wer sich anmeldet, kommt ohnehin
          gleich hier vorbei. */}
      <Neufassung />

      <header className="sticky top-0 z-20 border-b-2 border-slate-200 bg-white">
        {/* Der schmale orange Streifen ist das Erkennungszeichen von
            spuer.net. Er kostet 3 Pixel und macht die App auf den ersten
            Blick als Bernds Anwendung erkennbar. */}
        <div aria-hidden className="h-[3px] bg-akzent-500" />

        <div className="mx-auto flex max-w-5xl items-center gap-2 px-3 py-2 sm:gap-3 sm:px-4">
          <Link to="/" aria-label="Zur Übersicht" className="shrink-0">
            <img src={wortmarke} alt="SPÜR" className="h-7 w-auto sm:h-8" />
          </Link>

          {/* Die Suche bekommt den ganzen freien Platz – sie ist der
              häufigste Handgriff, häufiger als jeder Menüpunkt. */}
          <div className="min-w-0 flex-1">
            <Suche />
          </div>

          <Zustandsmarken online={online} stand={stand} ohneGegenprobe={ohneGegenprobe} />

          {/* Ein Menü statt einer Reihe unterstrichener Links. Auf jeder
              Bildschirmgröße dasselbe: so muss niemand lernen, dass etwas am
              Rechner oben steht und auf dem Handy woanders. */}
          <button
            type="button"
            className="knopf-still shrink-0 gap-2 px-2 sm:px-3"
            aria-label={menueOffen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={menueOffen}
            aria-haspopup="menu"
            onClick={() => setzeMenueOffen((o) => !o)}
          >
            {menueOffen ? <SymbolSchliessen /> : <SymbolMenue />}
            <span className="hidden max-w-32 truncate lg:inline">{konto?.name}</span>
          </button>
        </div>

        {/* Die Hauptbereiche am Rechner als Zeile unter der Kopfzeile.
            Unten am Bildschirmrand wären sie dort verschenkt: die Maus hat
            keinen Daumen, und der Weg nach unten ist weiter als der nach
            oben. Auf dem Handy übernimmt das die Leiste am unteren Rand. */}
        {!imBogen && (
          <nav
            aria-label="Hauptbereiche"
            className="hidden border-t border-slate-200 sm:block"
          >
            <div className="mx-auto flex max-w-5xl gap-1 px-4">
              {NAVIGATION.map((eintrag) => (
                <NavLink
                  key={eintrag.pfad}
                  to={eintrag.pfad}
                  end={eintrag.pfad === '/'}
                  className={({ isActive }) =>
                    `flex items-center gap-2 border-b-4 px-3 py-2 font-semibold ${
                      isActive
                        ? 'border-akzent-500 text-akzent-700'
                        : 'border-transparent text-slate-600 hover:text-marke-700'
                    }`
                  }
                >
                  <eintrag.Symbol groesse={20} />
                  {eintrag.text}
                </NavLink>
              ))}
            </div>
          </nav>
        )}
      </header>

      {menueOffen && (
        <Menue
          konto={konto}
          schliessen={() => setzeMenueOffen(false)}
          abmelden={abmelden}
          einfuehrungZeigen={() => {
            setzeMenueOffen(false)
            setzeEinfuehrung(true)
          }}
        />
      )}

      {/* Erscheint beim ersten Anmelden von selbst; über das Menü jederzeit
          erneut. */}
      <Einfuehrung erzwungen={einfuehrung} schliessen={() => setzeEinfuehrung(false)} />

      <main className="mx-auto w-full max-w-5xl flex-1 p-4 pb-28 sm:pb-8">{children}</main>

      {!imBogen && (
        <nav
          aria-label="Hauptbereiche"
          className="fixed inset-x-0 bottom-0 z-20 border-t-2 border-slate-200 bg-white sm:hidden"
        >
          <div
            className="mx-auto flex max-w-5xl"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            {NAVIGATION.map((eintrag) => (
              <NavLink
                key={eintrag.pfad}
                to={eintrag.pfad}
                end={eintrag.pfad === '/'}
                className={({ isActive }) =>
                  `flex flex-1 flex-col items-center justify-center gap-0.5 py-2 text-sm font-semibold ${
                    isActive
                      ? 'text-akzent-700 border-t-4 border-akzent-500 -mt-[2px]'
                      : 'text-slate-600'
                  }`
                }
                style={{ minHeight: '56px' }}
              >
                <eintrag.Symbol groesse={24} />
                {eintrag.text}
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  )
}

/**
 * Offline, wartende und abgelehnte Änderungen.
 *
 * Steht auch auf dem Handy neben der Suche, nicht im Burger: dass etwas nicht
 * gesendet werden konnte, muss man sehen, ohne ein Menü zu öffnen.
 */
function Zustandsmarken({
  online,
  stand,
  ohneGegenprobe,
}: {
  online: boolean
  stand: Sendestand
  ohneGegenprobe: boolean
}) {
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {!online && (
        <span
          className="marke bg-stufe-1-grund text-stufe-1"
          title={
            ohneGegenprobe
              ? 'Keine Verbindung – die Anmeldung konnte nicht geprüft werden. ' +
                'Änderungen werden gespeichert und später gesendet.'
              : 'Keine Verbindung – Änderungen werden gespeichert und später gesendet'
          }
        >
          offline
        </span>
      )}
      {stand.offen > 0 && (
        <button
          type="button"
          onClick={() => void abarbeiten()}
          className="marke bg-marke-100 text-marke-700"
          title="Wartende Änderungen jetzt senden"
        >
          {stand.laeuft ? 'sendet …' : stand.offen}
        </button>
      )}
      {stand.gescheitert > 0 && (
        <Link
          to="/warteschlange"
          className="marke bg-stufe-3-grund text-stufe-3"
          title={`${stand.gescheitert} Änderungen wurden abgelehnt`}
        >
          {stand.gescheitert} !
        </Link>
      )}
    </div>
  )
}

/**
 * Menü der Anwendung.
 *
 * Ein Menü für alle Bildschirmgrößen statt einer Linkreihe am Rechner und
 * eines Burgers auf dem Handy. Wer die App auf beiden benutzt, soll nicht
 * zweimal lernen, wo etwas liegt.
 *
 * Am Rechner hängt es rechts unter dem Knopf, auf dem Handy fällt es über die
 * ganze Breite – dort sind Tippziele wichtiger als Eleganz, und der Daumen
 * eines Prüfers mit Handschuh trifft keine 32-Pixel-Zeile.
 *
 * Die fünf Hauptbereiche stehen bewusst NICHT hier drin: die liegen unten in
 * der Leiste, wo der Daumen sie ohne Umweg erreicht. Hier steht, was seltener
 * gebraucht wird.
 */
function Menue({
  konto,
  schliessen,
  abmelden,
  einfuehrungZeigen,
}: {
  konto: ReturnType<typeof useKonto>['konto']
  schliessen: () => void
  abmelden: () => Promise<void>
  einfuehrungZeigen: () => void
}) {
  const eintraege = [
    {
      pfad: '/konto',
      text: 'Mein Konto',
      Symbol: SymbolPerson,
      hinweis: 'Name, Zertifikat, Passwort',
    },
    {
      pfad: '/bausteine',
      text: 'Kataloge',
      Symbol: SymbolKatalog,
      hinweis: 'Textbausteine und Kriterien',
    },
    {
      pfad: '/pruefablaeufe',
      text: 'Prüfabläufe',
      Symbol: SymbolAblauf,
      hinweis: 'Schrittfolgen je Gerätetyp',
    },
    {
      pfad: '/pruefpunkte',
      text: 'Prüfpunkte Spielplatz',
      Symbol: SymbolKatalog,
      hinweis: 'Platzbeurteilung nach DIN 18034',
    },
    /* Belege sichtbar für alle, die abrechnen dürfen. Ohne diesen Eintrag
       gab es die Abrechnung nur als Schnittstelle — sie war fertig gebaut
       und nicht erreichbar. */
    ...(darf(konto, 'belege_schreiben') || darf(konto, 'belege_lesen')
      ? [
          /* Vor den Belegen, weil es der frühere Schritt ist: erst
             sammeln, was fertig ist, dann das Dokument bearbeiten. */
          {
            pfad: '/abrechnen',
            text: 'Abrechnen',
            Symbol: SymbolKatalog,
            hinweis: 'Fertige Prüfungen zu einer Rechnung',
          },
          {
            pfad: '/belege',
            text: 'Belege',
            Symbol: SymbolKatalog,
            hinweis: 'Angebote und Rechnungen',
          },
        ]
      : []),
    {
      pfad: '/warteschlange',
      text: 'Warteschlange',
      Symbol: SymbolWarteschlange,
      hinweis: 'Was noch zum Server muss',
    },
    ...(darfVerwalten(konto)
      ? [
          {
            pfad: '/verwaltung',
            text: 'Verwaltung',
            Symbol: SymbolZahnrad,
            hinweis: 'Benutzer, Kundenzugänge, Unternehmen',
          },
        ]
      : []),
    /* Die Verwaltung der Anwendung selbst, nicht die eines Büros. Sie
       erscheint nur beim Betreiber – für ein Prüfbüro gibt es sie nicht,
       und ein Eintrag, der zu einer Absage führt, ist keine Auskunft. */
    ...(konto?.systemverwalter
      ? [
          {
            pfad: '/pruefbueros',
            text: 'Prüfbüros',
            Symbol: SymbolZahnrad,
            hinweis: 'Mandanten anlegen und sperren',
          },
        ]
      : []),
  ]

  return (
    <>
      {/* Klick daneben schließt. Auf dem Handy zusätzlich abgedunkelt, damit
          erkennbar ist, dass die Seite dahinter gerade nicht bedienbar ist. */}
      <button
        type="button"
        aria-label="Menü schließen"
        className="fixed inset-0 z-30 bg-slate-900/40 sm:bg-transparent"
        onClick={schliessen}
      />

      {/* Die Höhe ist begrenzt, nicht offen.
          `max-h-dvh` allein reichte nicht: am Rechner beginnt das Menü 4rem
          unter dem oberen Rand, durfte aber die ganze Bildschirmhöhe
          einnehmen – und lief damit unten aus dem Bild.

          Mit „Abrechnen" kam ein neunter Eintrag dazu, und bei zwei Spalten
          waren das fünf Reihen – auf einem 1280×720-Notebook eine zu viel.
          Ab `lg` deshalb drei Spalten in einem breiteren Fenster; der Test
          in `e2e/menue.spec.ts` misst das nach. */}
      <nav
        aria-label="Menü"
        className="fixed inset-x-0 top-0 z-40 max-h-dvh overflow-y-auto rounded-b-2xl bg-white p-4 shadow-lg
                   sm:inset-x-auto sm:right-4 sm:top-16 sm:max-h-[calc(100dvh-5rem)] sm:w-80
                   sm:rounded-2xl sm:border-2 sm:border-slate-200 md:w-[34rem] lg:w-[46rem]"
      >
        <div className="mb-3 flex items-start gap-3 border-b-2 border-slate-200 pb-3">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2">
              <span className="truncate font-bold">{konto?.name}</span>
              {konto && (
                <span className="marke bg-marke-100 text-marke-700">
                  {ROLLENTEXT[konto.rolle]}
                </span>
              )}
            </div>
            <div className="truncate text-base text-slate-600">{konto?.email}</div>
          </div>
          <button type="button" className="knopf-still px-3" onClick={schliessen}>
            <SymbolSchliessen />
            <span className="nur-vorlesen">Menü schließen</span>
          </button>
        </div>

        {/* Ab mittlerer Breite zwei Spalten. Das halbiert die Höhe, ohne die
            Erklärungszeilen zu opfern – die sind der Grund, dass niemand
            raten muss, was „Kataloge" von „Prüfpunkte Spielplatz"
            unterscheidet. Auf dem Handy bleibt es eine Spalte: dort ist die
            Trefferfläche für den Daumen wichtiger als die Höhe. */}
        <ul className="space-y-2 md:grid md:grid-cols-2 md:gap-2 md:space-y-0 lg:grid-cols-3">
          {eintraege.map((e) => (
            <li key={e.pfad}>
              <Link
                to={e.pfad}
                className="karte-knopf h-full items-start md:p-3"
                onClick={schliessen}
              >
                <span aria-hidden className="w-6 shrink-0 text-center text-xl">
                  <e.Symbol groesse={22} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{e.text}</span>
                  {/* Am Rechner eine Stufe kleiner. In der halbbreiten
                      Spalte bricht die Zeile sonst zweimal um, und der
                      Umbruch ist es, der das Menü zu hoch macht – nicht die
                      Zahl der Einträge. Auf dem Handy bleibt sie groß. */}
                  <span className="block text-base text-slate-600 md:text-sm">
                    {e.hinweis}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>

        <div className="mt-3 space-y-2 md:flex md:gap-2 md:space-y-0">
          <button
            type="button"
            className="knopf-zweit w-full md:flex-1"
            onClick={einfuehrungZeigen}
          >
            Einführung noch einmal ansehen
          </button>

          <button
            type="button"
            className="knopf-still w-full md:flex-1"
            onClick={() => {
              schliessen()
              void abmelden()
            }}
          >
            Abmelden
          </button>
        </div>

        {/* Die Fassung, ganz unten und leise.
            Sie steht hier, weil die erste Frage bei jeder Rückmeldung lautet:
            welche Fassung haben Sie? Der Wert wird beim Bauen eingesetzt,
            gilt also für genau diese Oberfläche. */}
        <p className="mt-4 text-center text-sm text-slate-500 md:mt-3">
          Fassung {import.meta.env.VITE_FASSUNG || 'Entwicklung'}
        </p>
      </nav>
    </>
  )
}
